import {Component, inject, OnInit} from '@angular/core';
import {NgIf} from "@angular/common";
import {FormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatDialogRef} from "@angular/material/dialog";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {Router} from "@angular/router";
import {GatewayEnvironment} from "../../../model/gateway/gatewayEnvironment";
import {HubEnvironmentOption} from "../../../model/gateway/hubEnvironmentOption";
import {GatewayOrderCreateRequest} from "../../../model/gateway/gatewayOrderCreateRequest";
import {GatewayOrderType} from "../../../model/gateway/gatewayOrderType";
import {GatewayEnvironmentService} from "../../../service/gateway.environment.service";
import {GatewayOrderService} from "../../../service/gateway.order.service";

@Component({
  selector: 'app-gateway-order-create',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    MatOption,
    MatSelect
  ],
  templateUrl: './gateway-order-create.component.html',
  styleUrl: './gateway-order-create.component.css'
})
export class GatewayOrderCreateComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private gatewayEnvironmentService = inject(GatewayEnvironmentService);
  private gatewayOrderService = inject(GatewayOrderService);
  private matDialogRef = inject(MatDialogRef<GatewayOrderCreateComponent>);
  private router = inject(Router);

  protected readonly GatewayOrderType = GatewayOrderType;

  gatewayOrderForm = this.buildForm();
  gatewayEnvironments: GatewayEnvironment[] = [];
  hubEnvironmentOptions: HubEnvironmentOption[] = [];
  environmentLoadError: string | null = null;
  hubEnvironmentLoadError: string | null = null;

  ngOnInit() {
    this.gatewayEnvironmentService.findAllActive().subscribe({
      next: gatewayEnvironments => {
        this.gatewayEnvironments = gatewayEnvironments;

        if (gatewayEnvironments.length > 0) {
          this.environmentKey?.setValue(gatewayEnvironments[0].environmentKey);
        } else {
          this.environmentKey?.setValue(null);
          this.environmentLoadError = 'No active gateway environments are configured.';
        }
      },
      error: () => {
        this.environmentKey?.setValue(null);
        this.environmentLoadError = 'Unable to load gateway environments.';
      }
    });

    this.gatewayOrderService.findHubEnvironments().subscribe({
      next: hubEnvironmentOptions => {
        this.hubEnvironmentOptions = hubEnvironmentOptions;

        if (hubEnvironmentOptions.length > 0) {
          this.hubTenantName?.setValue(hubEnvironmentOptions[0].value);
        } else {
          this.hubTenantName?.setValue(null);
          this.hubEnvironmentLoadError = 'No gateway hub environments are configured.';
        }
      },
      error: () => {
        this.hubTenantName?.setValue(null);
        this.hubEnvironmentLoadError = 'Unable to load gateway hub environments.';
      }
    });
  }

  submit() {
    const request = new GatewayOrderCreateRequest(
      this.environmentKey?.value!,
      this.orderName?.value!,
      this.gatewayOrderType?.value!,
      this.hubTenantName?.value!,
    );

    this.gatewayOrderService.createGatewayOrder(request).subscribe({
      next: () => {
        this.matDialogRef.close();
        void this.router.navigate(['gateway']);
      }
    });
  }

  prevent(event: any) {
    event.preventDefault();
  }

  cancel() {
    this.matDialogRef.close();
  }

  private buildForm() {
    return this.formBuilder.group({
      orderName: [null, [Validators.required, Validators.maxLength(100)]],
      hubTenantName: [null as string | null, [Validators.required]],
      gatewayOrderType: [GatewayOrderType.CREATE_DEVICES, [Validators.required]],
      environmentKey: [null as string | null, [Validators.required]],
    });
  }

  get orderName() {
    return this.gatewayOrderForm.get('orderName');
  }

  get hubTenantName() {
    return this.gatewayOrderForm.get('hubTenantName');
  }

  get gatewayOrderType() {
    return this.gatewayOrderForm.get('gatewayOrderType');
  }

  get environmentKey() {
    return this.gatewayOrderForm.get('environmentKey');
  }
}
